import React, { useEffect, useRef, useState } from 'react';
import { Star } from 'lucide-react';
import { cn } from '../../lib/utils';
import './effects/LandingEffects.css';

const TESTIMONIALS = [
  {
    name: 'Sarah Chen',
    role: 'Technical Project Manager',
    company: 'InnovateTech Solutions',
    image:
      'https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&q=80&w=150&h=150',
    content:
      "Zentime has transformed our project management approach. The AI's ability to adapt to different work" +
      ' styles while maintaining context across all interactions is remarkable. The new task board and note-taking features have made it our go-to tool for project organization.',
  },
  {
    name: 'Marcus Rodriguez',
    role: 'Lead Developer',
    company: 'CodeCraft Labs',
    image:
      'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&q=80&w=150&h=150',
    content:
      "The flexibility to switch between chat, notes, and task boards while maintaining context is a game-changer. Voice commands and document analysis save hours of work, and the AI's understanding of project context is consistently impressive.",
  },
  {
    name: 'Emily Thompson',
    role: 'Product Owner',
    company: 'Agile Dynamics',
    image:
      'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&q=80&w=150&h=150',
    content:
      "What sets Zentime apart is its intelligent adaptation to our workflow. Whether we're brainstorming in" +
      ' chat, organizing tasks on boards, or taking meeting notes, everything stays connected and contextually relevant.',
  },
];

export function Testimonials() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      {
        threshold: 0.2,
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.disconnect();
      }
    };
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-32" ref={sectionRef}>
      <h2 
        className={cn(
          "text-4xl font-bold text-white text-center mb-6 font-['Sora'] opacity-0 transform",
          isVisible && "animate-fade-in"
        )}
      >
        What Our Users Say
      </h2>
      <p 
        className={cn(
          "text-gray-400 text-center mb-16 text-xl max-w-3xl mx-auto opacity-0 transform",
          isVisible && "animate-fade-in"
        )}
        style={{ animationDelay: "200ms" }}
      >
        Join the flow, you'll never look back!
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {TESTIMONIALS.map((testimonial, index) => (
          <div
            key={testimonial.name}
            className={cn(
              "glow-card inner-light z-depth-1 inner-border-glow rounded-2xl p-8 flex flex-col opacity-0 transform relative overflow-hidden backdrop-blur-md",
              isVisible && "animate-fade-in-up"
            )}
            style={{ 
              animationDelay: `${(index + 1) * 200}ms`,
              background: index === 0 
                ? "linear-gradient(135deg, rgba(59, 130, 246, 0.15), rgba(37, 99, 235, 0.05))" 
                : index === 1 
                  ? "linear-gradient(135deg, rgba(139, 92, 246, 0.15), rgba(124, 58, 237, 0.05))" 
                  : "linear-gradient(135deg, rgba(236, 72, 153, 0.15), rgba(219, 39, 119, 0.05))",
              backdropFilter: "blur(12px)",
              transition: "all 0.5s ease"
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = index === 0 
                ? "linear-gradient(135deg, rgba(59, 130, 246, 0.25), rgba(37, 99, 235, 0.15))" 
                : index === 1 
                  ? "linear-gradient(135deg, rgba(139, 92, 246, 0.25), rgba(124, 58, 237, 0.15))" 
                  : "linear-gradient(135deg, rgba(236, 72, 153, 0.25), rgba(219, 39, 119, 0.15))";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = index === 0 
                ? "linear-gradient(135deg, rgba(59, 130, 246, 0.15), rgba(37, 99, 235, 0.05))" 
                : index === 1 
                  ? "linear-gradient(135deg, rgba(139, 92, 246, 0.15), rgba(124, 58, 237, 0.05))" 
                  : "linear-gradient(135deg, rgba(236, 72, 153, 0.15), rgba(219, 39, 119, 0.05))";
            }}
          >
            <div className="flex items-center gap-2 mb-6">
              {[...Array(5)].map((_, i) => (
                <div 
                  key={i} 
                  className="relative"
                  style={{ 
                    animation: `star-pulse 3s infinite ${i * 0.5}s`,
                  }}
                >
                  <Star 
                    size={20} 
                    className="text-yellow-500 fill-yellow-500 relative z-10" 
                  />
                  <div 
                    className="absolute inset-0 bg-yellow-500/30 blur-md rounded-full -z-10"
                    style={{
                      transform: 'scale(1.3)',
                      animation: `glow-pulse 3s infinite ${i * 0.5}s`
                    }}
                  />
                </div>
              ))}
            </div>
            
            {/* Animation keyframes now defined in LandingEffects.css */}
            {/* Add decorative light element in corner */}
            <div 
              className="absolute top-0 right-0 w-48 h-48 -mt-16 -mr-16 opacity-30 blur-xl transition-all duration-500"
              style={{
                background: index === 0 
                  ? "radial-gradient(circle, rgba(59, 130, 246, 0.6) 0%, transparent 70%)" 
                  : index === 1 
                    ? "radial-gradient(circle, rgba(139, 92, 246, 0.6) 0%, transparent 70%)" 
                    : "radial-gradient(circle, rgba(236, 72, 153, 0.6) 0%, transparent 70%)",
                animation: "pulse-light-slow 15s infinite linear"
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.opacity = "0.6"; 
                e.currentTarget.style.filter = "blur(8px)";
                e.currentTarget.style.animationName = "pulse-light-fast";
                e.currentTarget.style.animationDuration = "6s";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.opacity = "0.3";
                e.currentTarget.style.filter = "blur(16px)";
                e.currentTarget.style.animationName = "pulse-light-slow";
                e.currentTarget.style.animationDuration = "15s";
              }}
            />
            
            <p className="text-gray-100 text-lg flex-1 mb-8 leading-relaxed font-light">
              "{testimonial.content}"
            </p>
            {/* Animation keyframes now defined in LandingEffects.css */}
            <div className="flex items-center gap-4 relative z-10">
              {/* Image with glow effect */}
              <div className="relative">
                <img
                  src={testimonial.image}
                  alt={testimonial.name}
                  className="w-14 h-14 rounded-full object-cover border-2 border-white/20 z-10 relative"
                />
                <div 
                  className="absolute inset-0 blur-md rounded-full -z-10"
                  style={{
                    background: index === 0 
                      ? "radial-gradient(circle, rgba(59, 130, 246, 0.5) 30%, transparent 70%)" 
                      : index === 1 
                        ? "radial-gradient(circle, rgba(139, 92, 246, 0.5) 30%, transparent 70%)" 
                        : "radial-gradient(circle, rgba(236, 72, 153, 0.5) 30%, transparent 70%)",
                    transform: 'scale(1.2)',
                    animation: "subtle-pulse 4s infinite"
                  }}
                />
              </div>
              
              <div>
                <h4 className="text-white font-medium text-lg tracking-wide">{testimonial.name}</h4>
                <p className="text-gray-200">{testimonial.role}</p>
                <p 
                  className="font-medium" 
                  style={{ 
                    color: index === 0 
                      ? "rgba(191, 219, 254, 1)"  // blue-200 brighter
                      : index === 1 
                        ? "rgba(221, 214, 254, 1)"  // purple-200 brighter
                        : "rgba(251, 207, 232, 1)"  // pink-200 brighter
                  }}
                >
                  {testimonial.company}
                </p>
              </div>
              {/* Animation keyframes now defined in LandingEffects.css */}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
